import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { getServiceBaseURL } from 'APIPackage/MainModule/common/getServiceBaseURL';
import { APIServiceEnum } from 'APIPackage/MainModule/enums/APIServiceEnum';
import { apiGet } from 'APIPackage/MainModule/common/apiTools';
import { IApplicationConditionSubject } from 'APIPackage/AccountModule/interfaces/IApplicationConditionSubject';

import { IDictionary } from '../interfaces/IDictionary';
import { IDictionaryValues } from '../interfaces/IDictionaryValues';
import { IDictionaryVersion } from '../interfaces/IDictionaryVersion';
import { IKnmKind } from '../interfaces/IKnmKind';
import { IKnoOrganizationDictionary } from '../interfaces/IKnoOrganizationDictionary';
import { IPermitDocs } from '../interfaces/IPermitDocs';
import { IProfKind } from '../interfaces/IProfKind';
import { IRiskCategory248 } from '../interfaces/IRiskCategory248';
import { IPosition } from '../interfaces/IPosition';
import { IParticipantPosition } from '../interfaces/IParticipantPosition';
import { ISubjectType } from '../interfaces/ISubjectType';
import { ISubjectKind } from '../interfaces/ISubjectKind';
import { IEventType } from '../interfaces/IEventType';
import { ISupervisionType } from '../interfaces/ISupervisionType';
import { IAcceptedDocument } from '../interfaces/IAcceptedDocument';
import { IChecklistDictionary } from '../interfaces/IChecklistDictionary';
import { IRiskIndicatory } from '../interfaces/IRiskIndicatory';
import { IRequirement } from '../interfaces/IRequirement';
import { IDangerClass248 } from '../interfaces/IDangerClass248';
import { ITargetEntity } from '../interfaces/ITargetEntity';
import { IControlWithDate } from '../interfaces/IControlWithDate';
import { createPaginationParams } from '../../MainModule/common/createPaginationParams';
import { IKnoOrganisationsChildrenResponse } from '../../CatalogsModule/interfaces/IKnoOrganisationsChildrenResponse';
import { IPaginationParams } from '../../MainModule/interfaces/IPaginationParams';
import { IDecisionKind } from '../interfaces/IDecisionKind';
import { formatDate } from '../../../CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { IUserOrganisation } from '../../AccountModule/interfaces/IUserOrganisation';

interface IFetchKnoOrganisationsSearch extends IPaginationParams {
  query: string;
  adminLevel: string;
  searchCriteria?: string;
}

interface IFetchKnoOrganisationsChildren extends IPaginationParams {
  adminLevel: string;
  id?: string;
}

function knoSupervisionStartDateConverter(knoSupervisionData: IControlWithDate): IControlWithDate {
  return {
    ...knoSupervisionData,
    controlStartDate: formatDate({ date: knoSupervisionData.controlStartDate || '', dateFormat: DATE_FORMAT_LONG_FNS }),
  };
}

export async function fetchDictionariesConstraintByUUIDs(knoUUIDs: string[]): Promise<string[]> {
  const queryParams = new URLSearchParams();

  knoUUIDs.forEach(knoUUID => queryParams.append('knoUUIDs', knoUUID));

  return apiGet<string[]>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/constraint/by-uuids?${queryParams}`,
  );
}

export async function fetchDictionaryKnoSupervision(
  knoVersionId?: string,
  supervisionVersionId?: string,
): Promise<IControlWithDate> {
  const queryParams = new URLSearchParams();

  knoVersionId && queryParams.append('knoVersionId', knoVersionId);
  supervisionVersionId && queryParams.append('supervisionVersionId', supervisionVersionId);

  return apiGet<IControlWithDate>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/constraint/get-kno-supervision?${
      queryParams.toString().length ? `${queryParams}` : ''
    }`,
  ).then(value => knoSupervisionStartDateConverter(value));
}

export async function fetchGetKnoSupervisionUuid(knoVersionId: string, supervisionVersionId: string): Promise<string> {
  const queryParams = new URLSearchParams();

  queryParams.append('knoVersionId', knoVersionId);
  queryParams.append('supervisionVersionId', supervisionVersionId);

  return apiGet<string>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/constraint/get-kno-supervision-uuid?${queryParams}`,
  );
}

export async function fetchGetKnoSupervisionParentUuid(knoVersionId: string): Promise<string> {
  const queryParams = new URLSearchParams();

  queryParams.append('knoVersionId', knoVersionId);

  return apiGet<string>(
    `${getServiceBaseURL(
      APIServiceEnum.BASE_SERVICE,
    )}/dictionaries/constraint/get-kno-supervision-parent-uuid?${queryParams}`,
  );
}

export async function fetchGetEventUuid(
  knoVersionId: string,
  supervisionVersionId: string,
  knmKindVersionId: string,
): Promise<string[]> {
  const queryParams = new URLSearchParams();

  queryParams.append('knoVersionId', knoVersionId);
  queryParams.append('supervisionVersionId', supervisionVersionId);
  queryParams.append('knmKindId', knmKindVersionId);

  return apiGet<string[]>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/constraint/get-event-uuid?${queryParams}`,
  );
}

export async function fetchGetEventHasUnplannedActivity(
  knoVersionId: string,
  supervisionVersionId: string,
  knmKindVersionId: string,
): Promise<boolean> {
  const queryParams = new URLSearchParams();

  queryParams.append('knoVersionId', knoVersionId);
  queryParams.append('supervisionVersionId', supervisionVersionId);
  queryParams.append('knmKindVersionId', knmKindVersionId);

  return apiGet<boolean>(
    `${getServiceBaseURL(
      APIServiceEnum.BASE_SERVICE,
    )}/dictionaries/constraint/get-event-has-unplanned-activity?${queryParams}`,
  );
}

export async function fetchAllDictionaries(): Promise<IDictionary[]> {
  return apiGet<IDictionary[]>(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/get-all-dictionaries`);
}

export async function fetchDictionaryValues(
  dictionaryId: string,
  dictionaryVersion: number,
): Promise<IDictionaryVersion[]> {
  return apiGet<IDictionaryVersion[]>(
    `${getServiceBaseURL(
      APIServiceEnum.BASE_SERVICE,
    )}/dictionaries/get-dictionary-values/${dictionaryId}/${dictionaryVersion}`,
  );
}

export async function fetchDictionaryValue(
  dictionaryUUID: DictionaryUUIDsEnum,
  dictionaryVersionUUID: string,
): Promise<IDictionaryVersion> {
  return apiGet<IDictionaryVersion>(
    `${getServiceBaseURL(
      APIServiceEnum.BASE_SERVICE,
    )}/dictionaries/get-dictionary-value/${dictionaryUUID}/${dictionaryVersionUUID}`,
  );
}

export async function fetchKnoOrganizationsDictionary(): Promise<IDictionaryValues<IKnoOrganizationDictionary>> {
  return apiGet<IDictionaryValues<IKnoOrganizationDictionary>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/kno-organizations`,
  );
}

export async function fetchPositionsDictionary(uniqueUuid?: string): Promise<IDictionaryValues<IPosition>> {
  const queryParams = new URLSearchParams();

  uniqueUuid && queryParams.append('uniqueUuid', uniqueUuid);

  return apiGet<IDictionaryValues<IPosition>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/positions${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

/**
 * Запрос на получение списка разрешительных документов через id вида контроля
 */
export async function fetchPermitDocsDictionary(controlKindUuid?: string, uuidKno?: string): Promise<IPermitDocs[]> {
  const queryParams = new URLSearchParams();

  if (!uuidKno) {
    controlKindUuid && queryParams.append('controlKindId', controlKindUuid);
  } else {
    controlKindUuid && queryParams.append('uuidSupervision', controlKindUuid);
    uuidKno && queryParams.append('uuidKno', uuidKno);
  }

  return apiGet<IPermitDocs[]>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/ervk-control-kind-doc-target${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

export async function findDocTargetsDictionary(query: string): Promise<IPermitDocs[]> {
  return apiGet(
    `${getServiceBaseURL(
      APIServiceEnum.BASE_SERVICE,
      false,
    )}/dictionaries/find-ervk-control-kind-doc-target?query=${query}`,
  );
}

export async function fetchParticipantPositionsDictionary(
  uniqueUuid?: string,
): Promise<IDictionaryValues<IParticipantPosition>> {
  const queryParams = new URLSearchParams();

  uniqueUuid && queryParams.append('uniqueUuid', uniqueUuid);

  return apiGet<IDictionaryValues<IParticipantPosition>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/participant-positions${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

export async function fetchKnmKindsDictionary(
  knoVersionId?: string,
  supervisionVersionId?: string,
  conditionUuid?: string,
): Promise<IDictionaryValues<IKnmKind>> {
  const queryParams = new URLSearchParams();

  knoVersionId && queryParams.append('knoVersionId', knoVersionId);
  supervisionVersionId && queryParams.append('supervisionVersionId', supervisionVersionId);
  conditionUuid && queryParams.append('conditionUuid', conditionUuid);

  return apiGet<IDictionaryValues<IKnmKind>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/knm-kind${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

export async function fetchGetDecisionKindsDictionary(
  knoVersionId?: string,
  supervisionVersionId?: string,
): Promise<IDictionaryValues<IDecisionKind>> {
  const queryParams = new URLSearchParams();

  knoVersionId && queryParams.append('knoVersionId', knoVersionId);
  supervisionVersionId && queryParams.append('supervisionVersionId', supervisionVersionId);

  return apiGet<IDictionaryValues<IDecisionKind>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/decision-kind?${queryParams}`,
  );
}

export async function fetchGetDecisionKindsAllDictionary(
  knoVersionId?: string,
  supervisionVersionId?: string,
): Promise<IDictionaryValues<IDecisionKind>> {
  const queryParams = new URLSearchParams();

  knoVersionId && queryParams.append('knoVersionId', knoVersionId);
  supervisionVersionId && queryParams.append('supervisionVersionId', supervisionVersionId);

  return apiGet<IDictionaryValues<IDecisionKind>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/decision-kind-all?${queryParams}`,
  );
}

export async function fetchProfKindsDictionary(
  knoVersionId?: string,
  supervisionVersionId?: string,
  conditionUuid?: string,
): Promise<IDictionaryValues<IProfKind>> {
  if (!knoVersionId && !supervisionVersionId) {
    return apiGet<IDictionaryValues<IProfKind>>(
      `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/prof-kind-all`,
    );
  }

  const queryParams = new URLSearchParams();

  knoVersionId && queryParams.append('knoVersionId', knoVersionId);
  supervisionVersionId && queryParams.append('supervisionVersionId', supervisionVersionId);
  conditionUuid && queryParams.append('conditionUuid', conditionUuid);

  return apiGet<IDictionaryValues<IProfKind>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/prof-kind${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

export async function fetchRiskCategoriesDictionary(
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionUuid: string | undefined,
  knmKindUuid: string | undefined,
  isPlanned: boolean | undefined,
): Promise<IDictionaryValues<IRiskCategory248>> {
  const queryParams = new URLSearchParams();

  knoSupervisionUuid && queryParams.append('uniqueUuid', knoSupervisionUuid);
  if (orderVersionUuid) {
    queryParams.append('orderVersionUuid', orderVersionUuid);
  } else {
    conditionUuid && queryParams.append('conditionUuid', conditionUuid);

    // knmKindUuid && queryParams.append('knmKindUuid', knmKindUuid);
  }
  typeof isPlanned === 'boolean' && queryParams.append('plannedKnm', String(isPlanned));

  return apiGet<IDictionaryValues<IDangerClass248>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/risk-category${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

export async function fetchDangerClassesDictionary(
  uniqueUuid?: string,
  conditionUuid?: string,
  orderVersionUuid?: string,
  isPlanned?: boolean,
): Promise<IDictionaryValues<IDangerClass248>> {
  const queryParams = new URLSearchParams();

  uniqueUuid && queryParams.append('uniqueUuid', uniqueUuid);
  if (orderVersionUuid) {
    queryParams.append('orderVersionUuid', orderVersionUuid);
  } else {
    conditionUuid && queryParams.append('conditionUuid', conditionUuid);
  }
  typeof isPlanned === 'boolean' && queryParams.append('plannedKnm', String(isPlanned));

  return apiGet<IDictionaryValues<IDangerClass248>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/danger-class${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

export async function fetchSubjectTypesDictionary(
  uniqueUuid?: string,
  conditionUuid?: string,
  orderVersionUuid?: string,
): Promise<IDictionaryValues<ISubjectType>> {
  const queryParams = new URLSearchParams();

  uniqueUuid && queryParams.append('uniqueUuid', uniqueUuid);
  if (orderVersionUuid) {
    queryParams.append('orderVersionUuid', orderVersionUuid);
  } else {
    conditionUuid && queryParams.append('conditionUuid', conditionUuid);
  }

  return apiGet<IDictionaryValues<ISubjectType>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/subject-type${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

export async function fetchSubjectKindsDictionary(
  uniqueUuid?: string,
  conditionUuid?: string,
  orderVersionUuid?: string,
  objectTypeDictVersionId?: string,
): Promise<IDictionaryValues<ISubjectKind>> {
  const queryParams = new URLSearchParams();

  uniqueUuid && queryParams.append('uniqueUuid', uniqueUuid);
  if (orderVersionUuid) {
    queryParams.append('orderVersionUuid', orderVersionUuid);
  } else {
    conditionUuid && queryParams.append('conditionUuid', conditionUuid);
  }

  if (objectTypeDictVersionId) {
    queryParams.append('objectTypeDictVersionId', objectTypeDictVersionId);
  }

  return apiGet<IDictionaryValues<ISubjectKind>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/subject-kind${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

export async function fetchSubjectSubKindsDictionary(
  uniqueUuid?: string,
  conditionUuid?: string,
  orderVersionUuid?: string,
  objectTypeDictVersionId?: string,
): Promise<IDictionaryValues<ISubjectType>> {
  const queryParams = new URLSearchParams();

  uniqueUuid && queryParams.append('uniqueUuid', uniqueUuid);
  if (orderVersionUuid) {
    queryParams.append('orderVersionUuid', orderVersionUuid);
  } else {
    conditionUuid && queryParams.append('conditionUuid', conditionUuid);
  }

  if (objectTypeDictVersionId) {
    queryParams.append('objectTypeDictVersionId', objectTypeDictVersionId);
  }

  return apiGet<IDictionaryValues<ISubjectType>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/subject-sub-kind${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

export async function fetchEventTypesDictionary(
  uniqueUuid?: string,
  conditionUuid?: string,
  orderVersionUuid?: string,
): Promise<IDictionaryValues<IEventType>> {
  const queryParams = new URLSearchParams();

  uniqueUuid && queryParams.append('uniqueUuid', uniqueUuid);
  if (orderVersionUuid) {
    queryParams.append('orderVersionUuid', orderVersionUuid);
  } else {
    conditionUuid && queryParams.append('conditionUuid', conditionUuid);
  }

  return apiGet<IDictionaryValues<ISubjectType>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/event-type${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

export async function fetchPmEventTypesDictionary(
  uniqueUuid: string | undefined,
): Promise<IDictionaryValues<IEventType>> {
  const queryParams = new URLSearchParams();

  uniqueUuid && queryParams.append('uniqueUuid', uniqueUuid);

  return apiGet<IDictionaryValues<ISubjectType>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/pm-events${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

export async function fetchPmProfEventTypesDictionary(
  isUnableToRefuse: boolean | undefined,
): Promise<IDictionaryValues<IEventType>> {
  const queryParams = new URLSearchParams();

  queryParams.append('isUnableToRefuse', isUnableToRefuse ? 'true' : 'false');

  return apiGet<IDictionaryValues<ISubjectType>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/pm-prof-events${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

export async function fetchSupervisionTypesDictionary(
  uuids: string[],
  plannedActivities?: boolean,
  federalLaws?: string[],
): Promise<IDictionaryValues<ISupervisionType>> {
  const queryParams = new URLSearchParams();

  uuids.forEach(uuid => queryParams.append('uuids', uuid));
  plannedActivities !== undefined && queryParams.append('plannedActivities', plannedActivities.toString());
  federalLaws && federalLaws.forEach(law => queryParams.append('federalLaw', law));

  return apiGet<IDictionaryValues<ISupervisionType>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/supervision-type?${queryParams}`,
  );
}

export async function fetchSupervisionTypeBySubjectTypeDictionary(
  subjectTypeUuid: string,
): Promise<IDictionaryValues<ISupervisionType>> {
  const queryParams = new URLSearchParams();

  queryParams.append('subjectType', subjectTypeUuid);

  return apiGet<IDictionaryValues<ISupervisionType>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/supervision-type-by-subject-type?${queryParams}`,
  );
}

export async function fetchAcceptedDocumentsDictionary(
  knoVersionId?: string,
  supervisionVersionId?: string,
): Promise<IDictionaryValues<IAcceptedDocument>> {
  const queryParams = new URLSearchParams();

  knoVersionId && queryParams.append('knoVersionId', knoVersionId);
  supervisionVersionId && queryParams.append('supervisionVersionId', supervisionVersionId);

  return apiGet<IDictionaryValues<IAcceptedDocument>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/accepted-document${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

export async function fetchChecklistsDictionary(
  uuidKno: string,
  uuidSupervision: string,
): Promise<IDictionaryValues<IChecklistDictionary>> {
  const queryParams = new URLSearchParams({
    uuidKno,
    uuidSupervision,
  });

  return apiGet<IDictionaryValues<IChecklistDictionary>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/check-list?${queryParams}`,
  );
}

export async function fetchRiskIndicatorsDictionary(uniqueUuid?: string): Promise<IDictionaryValues<IRiskIndicatory>> {
  const queryParams = new URLSearchParams();

  uniqueUuid && queryParams.append('uniqueUuid', uniqueUuid);

  return apiGet<IDictionaryValues<IRiskIndicatory>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/risk-indicatory${
      queryParams.toString().length ? `?${queryParams}` : ''
    }`,
  );
}

export async function fetchRiskIndicatorsDictionaryByQuery(query: string): Promise<IDictionaryValues<IRiskIndicatory>> {
  const queryParams = new URLSearchParams();

  query && queryParams.append('query', query);

  return apiGet<IDictionaryValues<IRiskIndicatory>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/find-risk-indicators?${queryParams}`,
  );
}

export async function fetchRequirementsDictionary(
  uuidKno: string,
  uuidSupervision: string,
): Promise<IDictionaryValues<IRequirement>> {
  const queryParams = new URLSearchParams({
    uuidKno,
    uuidSupervision,
  });

  return apiGet<IDictionaryValues<IRequirement>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/requirement?${queryParams}`,
  );
}

export async function fetchTargetEntityDictionary(
  knoVersionId?: string,
  supervisionVersionId?: string,
  conditionUuid?: string,
  orderVersionUuid?: string,
): Promise<IDictionaryValues<ITargetEntity>> {
  const queryParams = new URLSearchParams();

  if (!knoVersionId && !supervisionVersionId && !conditionUuid && !orderVersionUuid) {
    return apiGet<IDictionaryValues<ITargetEntity>>(
      `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/target-entity-all`,
    );
  }
  knoVersionId && queryParams.append('knoVersionId', knoVersionId);
  supervisionVersionId && queryParams.append('supervisionVersionId', supervisionVersionId);
  if (orderVersionUuid) {
    queryParams.append('orderVersionUuid', orderVersionUuid);
  } else {
    conditionUuid && queryParams.append('conditionUuid', conditionUuid);
  }

  return apiGet<IDictionaryValues<ITargetEntity>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/target-entity?${
      queryParams.toString().length ? `${queryParams}` : ''
    }`,
  );
}

export async function fetchTargetEntityFounderDictionary(): Promise<IDictionaryValues<ITargetEntity>> {
  return apiGet<IDictionaryValues<ITargetEntity>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/target-entity-founder`,
  );
}

export async function fetchSubjectTypesMultiDictionary(
  knoVersionIds: string[],
  supervisionVersionIds: string[],
): Promise<IDictionaryValues<ISubjectType>> {
  const queryParams = new URLSearchParams();

  knoVersionIds.forEach(uuid => queryParams.append('knoVersionIds', uuid));
  supervisionVersionIds.forEach(uuid => queryParams.append('supervisionVersionIds', uuid));
  queryParams.append('dictionary', 'SUBJECT_TYPE');

  return apiGet<IDictionaryValues<ISubjectType>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/dictionary-multi?${
      queryParams.toString().length ? `${queryParams}` : ''
    }`,
  );
}

export async function fetchSubjectKindsMultiDictionary(
  knoVersionIds: string[],
  supervisionVersionIds: string[],
): Promise<IDictionaryValues<ISubjectKind>> {
  const queryParams = new URLSearchParams();

  knoVersionIds.forEach(uuid => queryParams.append('knoVersionIds', uuid));
  supervisionVersionIds.forEach(uuid => queryParams.append('supervisionVersionIds', uuid));
  queryParams.append('dictionary', 'SUBJECT_KIND');

  return apiGet<IDictionaryValues<ISubjectKind>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/dictionary-multi?${
      queryParams.toString().length ? `${queryParams}` : ''
    }`,
  );
}

export async function fetchSubjectSubKindsMultiDictionary(
  knoVersionIds: string[],
  supervisionVersionIds: string[],
): Promise<IDictionaryValues<ISubjectKind>> {
  const queryParams = new URLSearchParams();

  knoVersionIds.forEach(uuid => queryParams.append('knoVersionIds', uuid));
  supervisionVersionIds.forEach(uuid => queryParams.append('supervisionVersionIds', uuid));
  queryParams.append('dictionary', 'SUBJECT_SUB_KIND');

  return apiGet<IDictionaryValues<ISubjectKind>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/dictionary-multi?${
      queryParams.toString().length ? `${queryParams}` : ''
    }`,
  );
}

export async function fetchDictionaryKnoOrganisationsSearch({
  query,
  adminLevel,
  ...paginationParams
}: IFetchKnoOrganisationsSearch): Promise<IKnoOrganisationsChildrenResponse> {
  const queryParams = new URLSearchParams();

  queryParams.append('query', query);
  queryParams.append('adminLevel', adminLevel.toString());
  const params = createPaginationParams(paginationParams);

  return apiGet<IKnoOrganisationsChildrenResponse>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/kno-organizations/search?${queryParams}&${params}`,
  );
}

export async function fetchDictionaryKnoOrganisationsChildren({
  adminLevel,
  id,
  ...paginationParams
}: IFetchKnoOrganisationsChildren): Promise<IKnoOrganisationsChildrenResponse> {
  const queryParams = new URLSearchParams();

  queryParams.append('adminLevel', adminLevel);
  if (id) {
    queryParams.append('organizationId', id.toString());
  }
  const params = createPaginationParams(paginationParams);

  return apiGet<IKnoOrganisationsChildrenResponse>(
    `${getServiceBaseURL(
      APIServiceEnum.BASE_SERVICE,
    )}/dictionaries/kno-organizations/children?${queryParams}&${params}`,
  );
}

export async function fetchKnoOrganizationWithTerritory(
  uuidKno: string,
  uuidSupervision: string,
): Promise<IUserOrganisation[]> {
  const queryParams = new URLSearchParams();

  queryParams.append('uuidKno', uuidKno);
  queryParams.append('uuidSupervision', uuidSupervision);

  return apiGet(
    `${getServiceBaseURL(
      APIServiceEnum.LK_SERVICE,
    )}/dictionaries/kno-organizations/territory-by-supervision?${queryParams}`,
  );
}

export async function fetchSupervisionDictionaryValue(
  knoDictionaryVersionUUID: string,
  supervisionDictionaryVersionUUID: string,
): Promise<IDictionaryVersion> {
  return apiGet<IDictionaryVersion>(
    `${getServiceBaseURL(
      APIServiceEnum.BASE_SERVICE,
    )}/dictionaries/get-supervision-dictionary-value/${knoDictionaryVersionUUID}/${supervisionDictionaryVersionUUID}`,
  );
}

export async function fetchApplicationConditionSubjects(
  conditionUuid?: string,
  orderVersionUuid?: string,
): Promise<IApplicationConditionSubject[]> {
  const queryParams = new URLSearchParams();

  if (orderVersionUuid) {
    queryParams.append('orderVersionUuid', orderVersionUuid);
  } else {
    conditionUuid && queryParams.append('conditionUuid', conditionUuid);
  }

  return apiGet(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/application-condition-subjects?${queryParams}`,
  );
}

export async function fetchSubjectKindsDictionaryByQuery(query: string): Promise<IDictionaryValues<ISubjectKind>> {
  const queryParams = new URLSearchParams();

  query && queryParams.append('query', query);

  return apiGet<IDictionaryValues<ISubjectKind>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/find-subject-kinds?${queryParams}`,
  );
}

export async function fetchSubjectSubKindsDictionaryByQuery(query: string): Promise<IDictionaryValues<ISubjectKind>> {
  const queryParams = new URLSearchParams();

  query && queryParams.append('query', query);

  return apiGet<IDictionaryValues<ISubjectKind>>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/find-subject-sub-kinds?${queryParams}`,
  );
}

export async function fetchIsUnableToRefuseFlagPm(
  reasonErvkUuid: string,
  orderId?: string,
  orderVersionId?: string,
): Promise<boolean> {
  const queryParams = new URLSearchParams();

  queryParams.append('reasonId', reasonErvkUuid);
  if (orderId && orderVersionId) {
    queryParams.append('orderId', orderId);
    queryParams.append('orderVersionId', orderVersionId);
  }

  return apiGet<boolean>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/dictionaries/get-is-unable-to-refuse-flag-pm?${
      queryParams.toString().length ? `${queryParams}` : ''
    }`,
  );
}
